/**
 * An image picked in the browser, made small enough to send: scaled so its
 * long edge is at most `maxEdge` px and re-encoded, as PNG while that stays
 * at most `pngMaxBytes`, else as JPEG. Refused past `maxBytes`. A Clu chat
 * screenshot and a signature photo each pass their own limits.
 */

export type ImageType = 'image/png' | 'image/jpeg';

type Decoded = { width: number; height: number; source: CanvasImageSource };

export type ImageCodec = {
  decode: (file: Blob) => Promise<Decoded & { close?: () => void }>;
  encode: (
    source: CanvasImageSource,
    width: number,
    height: number,
    type: ImageType,
    quality?: number
  ) => Promise<Blob>;
};

export type ImageLimits = {
  /** The file types taken. */
  types: readonly string[];
  /** The largest file taken, before it is decoded. */
  maxRawBytes: number;
  maxEdge: number;
  /** PNG while it weighs at most this; JPEG past it. */
  pngMaxBytes: number;
  maxBytes: number;
  jpegQuality: number;
  /** The result's name when the file has none. */
  fallbackName: string;
  tooLarge: string;
  wrongType: string;
};

/** Refused before anything is sent; the message is for the user. */
export class ImageRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageRefusedError';
  }
}

/** Scales width and height so the long edge is at most `maxEdge`. */
export function fitWithin(
  width: number,
  height: number,
  maxEdge: number
): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxEdge) return { width, height };
  const scale = maxEdge / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** A failed draw or encode; `prepareImage` refuses it as too large. */
class EncodeError extends Error {}

export const browserImageCodec: ImageCodec = {
  decode: async (file) => {
    const bitmap = await createImageBitmap(file);
    return {
      width: bitmap.width,
      height: bitmap.height,
      source: bitmap,
      close: () => bitmap.close(),
    };
  },
  encode: (source, width, height, type, quality) =>
    new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) {
        reject(new EncodeError());
        return;
      }
      // JPEG has no transparency: a see-through area would come out black.
      if (type === 'image/jpeg') {
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
      }
      context.drawImage(source, 0, 0, width, height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new EncodeError())),
        type,
        quality
      );
    }),
};

/** The file to send in place of `file`, or an `ImageRefusedError`. */
export async function prepareImage(
  file: File,
  limits: ImageLimits,
  codec: ImageCodec = browserImageCodec
): Promise<File> {
  if (!limits.types.includes(file.type)) {
    throw new ImageRefusedError(limits.wrongType);
  }
  if (file.size > limits.maxRawBytes) {
    throw new ImageRefusedError(limits.tooLarge);
  }
  const decoded = await codec.decode(file);
  try {
    const { width, height } = fitWithin(
      decoded.width,
      decoded.height,
      limits.maxEdge
    );
    const encode = (...format: [type: ImageType, quality?: number]) =>
      codec
        .encode(decoded.source, width, height, ...format)
        .catch((error: unknown) => {
          if (error instanceof EncodeError) {
            throw new ImageRefusedError(limits.tooLarge);
          }
          throw error;
        });
    let type: ImageType = 'image/png';
    let blob = await encode(type);
    if (blob.size > limits.pngMaxBytes) {
      type = 'image/jpeg';
      blob = await encode(type, limits.jpegQuality);
    }
    if (blob.size > limits.maxBytes) {
      throw new ImageRefusedError(limits.tooLarge);
    }
    const base = file.name.replace(/\.[^.]+$/, '') || limits.fallbackName;
    const extension = type === 'image/png' ? 'png' : 'jpg';
    return new File([blob], `${base}.${extension}`, { type });
  } finally {
    decoded.close?.();
  }
}
